import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { goalsService } from "@/services/goalsService";

interface GrowthGoal {
  id: string;
  title: string;
  description?: string;
  category: string;
  due_date?: string;
  progress: number;
  status?: 'active' | 'completed' | 'paused' | 'cancelled';
  priority?: 'low' | 'medium' | 'high';
  skills_needed?: string[];
}

interface EditGrowthGoalModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  goal: GrowthGoal;
  onUpdate: () => void;
}

const SKILL_SUGGESTIONS = [
  "React", "TypeScript", "Python", "Leadership", "Communication",
  "Project Management", "Data Analysis", "UI/UX Design", "DevOps",
  "Machine Learning", "Public Speaking", "Mentoring", "Strategic Thinking"
];

export default function EditGrowthGoalModal({
  isOpen,
  onOpenChange,
  goal,
  onUpdate,
}: EditGrowthGoalModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [targetDate, setTargetDate] = useState<Date>();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'active' | 'completed' | 'paused' | 'cancelled'>('active');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [skillsNeeded, setSkillsNeeded] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState("");

  // Initialize form with goal data
  useEffect(() => {
    if (goal && isOpen) {
      setTitle(goal.title);
      setDescription(goal.description || "");
      setCategory(goal.category);
      setTargetDate(goal.due_date ? new Date(goal.due_date) : undefined);
      setProgress(goal.progress);
      setStatus(goal.status || 'active');
      setPriority(goal.priority || 'medium');
      setSkillsNeeded(goal.skills_needed || []);
      setNewSkill("");
    }
  }, [goal, isOpen]);

  const addSkill = (skill: string) => {
    if (skill.trim() && !skillsNeeded.includes(skill.trim())) {
      setSkillsNeeded([...skillsNeeded, skill.trim()]);
      setNewSkill("");
    }
  };

  const removeSkill = (skill: string) => {
    setSkillsNeeded(skillsNeeded.filter(s => s !== skill));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    setIsSaving(true);
    try {
      // Format dueDate to YYYY-MM-DD if present
      let formattedDueDate: string | undefined;
      if (targetDate) {
        formattedDueDate = targetDate.toISOString().split('T')[0];
      }

      await goalsService.updateGoal(goal.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        dueDate: formattedDueDate,
        progress,
        status,
        priority,
        skillsNeeded: skillsNeeded,
      });

      toast.success("Goal updated successfully!");
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error('Failed to update goal');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredSuggestions = SKILL_SUGGESTIONS.filter(
    skill => 
      !skillsNeeded.includes(skill) &&
      skill.toLowerCase().includes(newSkill.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Growth Goal</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Title */}
          <div className="grid gap-2">
            <Label htmlFor="edit-title">Goal Title</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What do you want to achieve?"
            />
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your goal and what success looks like..."
              className="min-h-[80px]"
            />
          </div>

          {/* Category and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Technical">Technical</SelectItem>
                  <SelectItem value="Leadership">Leadership</SelectItem>
                  <SelectItem value="Communication">Communication</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(value: 'low' | 'medium' | 'high') => setPriority(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status and Progress */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(value: 'active' | 'completed' | 'paused' | 'cancelled') => setStatus(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Progress</Label>
              <div className="space-y-2">
                <Slider
                  value={[progress]}
                  onValueChange={(value) => setProgress(value[0])}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <div className="text-sm text-muted-foreground text-center">
                  {progress}%
                </div>
              </div>
            </div>
          </div>

          {/* Target Date */}
          <div className="grid gap-2">
            <Label>Target Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !targetDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {targetDate ? format(targetDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={targetDate}
                  onSelect={setTargetDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Skills Needed */}
          <div className="grid gap-2">
            <Label>Skills Needed</Label>
            
            {/* Add new skill */}
            <div className="flex gap-2">
              <Input
                placeholder="Add a skill..."
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill(newSkill);
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addSkill(newSkill)}
                disabled={!newSkill.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Skill suggestions */}
            {newSkill && filteredSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-muted/50">
                {filteredSuggestions.slice(0, 5).map((skill) => (
                  <Badge
                    key={skill}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground text-xs"
                    onClick={() => addSkill(skill)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {skill}
                  </Badge>
                ))}
              </div>
            )}

            {/* Selected skills */}
            {skillsNeeded.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg">
                {skillsNeeded.map((skill) => (
                  <Badge key={skill} variant="secondary">
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}